import { oc } from "@orpc/contract";
import type { ContractRouterClient } from "@orpc/contract";
import { z } from "zod";
import * as s from "./schemas";

export * from "./schemas";

export const contract = oc.router({
  auth: {
    me: oc.route({ method: "GET", path: "/api/v1/auth/me" }).output(z.object({ data: s.User })),

    updateProfile: oc
      .route({ method: "PUT", path: "/api/v1/auth/profile" })
      .input(s.UpdateProfileInput)
      .output(z.object({ data: s.User })),

    logout: oc
      .route({ method: "POST", path: "/api/v1/auth/logout" })
      .output(z.object({ success: z.literal(true) })),
  },

  user: {
    list: oc
      .route({ method: "GET", path: "/api/v1/users" })
      .input(
        s.PaginationRequest.extend({
          q: z.string().optional(),
          role: z.string().optional(),
          // 複数ロールでの絞り込み (例: ドライバー候補 = driver + admin)。role より優先する。
          roles: z.array(z.string()).optional(),
        }),
      )
      .output(
        z.object({
          data: z.array(s.User),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/users/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.User })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/users/:publicId" })
      .input(s.UpdateUserInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.User })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/users/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  order: {
    list: oc
      .route({ method: "GET", path: "/api/v1/orders" })
      .input(s.ListOrdersInput)
      .output(
        z.object({
          data: z.array(s.Order),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/orders/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Order })),

    create: oc
      .route({ method: "POST", path: "/api/v1/orders" })
      .input(s.CreateOrderInput)
      .output(z.object({ data: s.Order })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/orders/:publicId" })
      .input(s.UpdateOrderInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Order })),

    updateStatus: oc
      .route({ method: "PATCH", path: "/api/v1/orders/:publicId/status" })
      .input(s.UpdateOrderStatusInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Order })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/orders/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),

    listRevisions: oc
      .route({ method: "GET", path: "/api/v1/orders/:publicId/revisions" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.OrderRevision) })),

    listTimeline: oc
      .route({ method: "GET", path: "/api/v1/orders/:publicId/timeline" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.OrderTimelineEvent) })),

    /**
     * #689: 受注一覧から複数件をまとめて確定する (draft → confirmed のみ)。
     * 全件ロールバックせず、確定できた分はコミットし、失敗は理由付きで返す。
     */
    bulkConfirm: oc
      .route({ method: "POST", path: "/api/v1/orders/bulk-confirm" })
      .input(s.BulkConfirmOrdersInput)
      .output(s.BulkConfirmOrdersOutput),

    backfillPartialTimeWindows: oc
      .route({ method: "POST", path: "/api/v1/orders/backfill-partial-time-windows" })
      .input(s.BackfillPartialTimeWindowsInput)
      .output(s.BackfillPartialTimeWindowsOutput),
  },

  client: {
    list: oc
      .route({ method: "GET", path: "/api/v1/clients" })
      .input(s.ListClientsInput)
      .output(
        z.object({
          data: z.array(s.Client),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/clients/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Client })),

    create: oc
      .route({ method: "POST", path: "/api/v1/clients" })
      .input(s.CreateClientInput)
      .output(z.object({ data: s.Client })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/clients/:publicId" })
      .input(s.UpdateClientInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Client })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/clients/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  deliverySpot: {
    list: oc
      .route({ method: "GET", path: "/api/v1/delivery-spots" })
      .input(s.ListDeliverySpotsInput)
      .output(
        z.object({
          data: z.array(s.DeliverySpot),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/delivery-spots/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DeliverySpot })),

    create: oc
      .route({ method: "POST", path: "/api/v1/delivery-spots" })
      .input(s.CreateDeliverySpotInput)
      .output(z.object({ data: s.DeliverySpot })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/delivery-spots/:publicId" })
      .input(s.UpdateDeliverySpotInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DeliverySpot })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/delivery-spots/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  pickupSpot: {
    list: oc
      .route({ method: "GET", path: "/api/v1/pickup-spots" })
      .input(s.ListPickupSpotsInput)
      .output(
        z.object({
          data: z.array(s.PickupSpot),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/pickup-spots/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.PickupSpot })),

    create: oc
      .route({ method: "POST", path: "/api/v1/pickup-spots" })
      .input(s.CreatePickupSpotInput)
      .output(z.object({ data: s.PickupSpot })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/pickup-spots/:publicId" })
      .input(s.UpdatePickupSpotInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.PickupSpot })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/pickup-spots/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  vehicle: {
    list: oc
      .route({ method: "GET", path: "/api/v1/vehicles" })
      .input(s.ListVehiclesInput)
      .output(
        z.object({
          data: z.array(s.Vehicle),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/vehicles/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Vehicle })),

    create: oc
      .route({ method: "POST", path: "/api/v1/vehicles" })
      .input(s.CreateVehicleInput)
      .output(z.object({ data: s.Vehicle })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/vehicles/:publicId" })
      .input(s.UpdateVehicleInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Vehicle })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/vehicles/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  plan: {
    list: oc
      .route({ method: "GET", path: "/api/v1/plans" })
      .input(s.ListDeliveryPlansInput)
      .output(
        z.object({
          data: z.array(s.DeliveryPlan),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/plans/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DeliveryPlan })),

    create: oc
      .route({ method: "POST", path: "/api/v1/plans" })
      .input(s.CreateDeliveryPlanInput)
      .output(z.object({ data: s.DeliveryPlan })),

    // 便内の未確定 (draft/assigned) レーンを一括で confirmed にする (#399 でレーン単位確定の一括実体に再定義)。
    // 確定済みレーンは再確定しない。ドライバー未割当の未確定レーンが残っていれば DRIVER_NOT_ASSIGNED。
    confirm: oc
      .route({ method: "POST", path: "/api/v1/plans/:publicId/confirm" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.ConfirmDeliveryPlanResult })),

    // 便内の confirmed レーンを一括で assigned に戻す (#399)。started/completed はスキップして明示する。
    // 解除したレーンの担当ドライバーへ確定解除通知 (instruction_cancelled) を送る。
    unconfirm: oc
      .route({ method: "POST", path: "/api/v1/plans/:publicId/unconfirm" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.UnconfirmDeliveryPlanResult })),
  },

  route: {
    create: oc
      .route({ method: "POST", path: "/api/v1/routes" })
      .input(s.CreateRouteInput)
      .output(z.object({ data: s.Route })),

    listByPlan: oc
      .route({ method: "GET", path: "/api/v1/plans/:planPublicId/routes" })
      .input(z.object({ planPublicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.Route) })),

    get: oc
      .route({ method: "GET", path: "/api/v1/routes/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // ドライバー/車両割当. 同一 plan 内の重複割当は 409 DRIVER_ALREADY_ASSIGNED で拒否し (Issue #400)、
    // 車両の自動割当・スワップを原子的に行い、影響ルートと通知を返す.
    // 確定済み (confirmed 以降) レーンはロック (#399): 対象レーンが確定済みなら 422 ROUTE_LOCKED、
    // 指定車両が確定済みレーンで使用中 (スワップで奪う) なら 422 VEHICLE_LOCKED.
    assign: oc
      .route({ method: "PUT", path: "/api/v1/routes/:publicId/assign" })
      .input(s.AssignDriverInput.extend({ publicId: s.PublicIdSchema }))
      .output(
        z.object({
          data: s.Route,
          /** 副作用で driver/vehicle が変わった同一 plan 内の他ルート (最新状態) */
          affected_routes: z.array(s.Route).default([]),
          /** 旧レーンへの自動変更の通知 (UI トースト用) */
          notices: z.array(s.AssignNotice).default([]),
        }),
      ),

    // API-34: ドライバー割当解除。driver_id を null に戻し、assigned なら draft に遷移する。
    // 車両割当は維持する。confirmed 以降は ROUTE_LOCKED。同一 plan 内での付け替え
    // (旧レーン解除 → 新レーンで assign) の明示操作に使う (Issue #400)。
    unassignDriver: oc
      .route({ method: "DELETE", path: "/api/v1/routes/:publicId/assign" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // レーン単位の配車確定 (#399)。assigned → confirmed に遷移する。
    // plan.status は配下レーンから導出して更新する。
    // ドライバー未割当 (draft 含む) は DRIVER_NOT_ASSIGNED、車両未割当は VEHICLE_NOT_ASSIGNED、
    // 確定済み以降は ROUTE_ALREADY_CONFIRMED。
    confirm: oc
      .route({ method: "PUT", path: "/api/v1/routes/:publicId/confirm" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // レーン単位の配車確定解除 (#399)。confirmed → assigned に戻す。
    // plan.status は配下レーンから導出して更新する。個別解除では通知しない。
    // started/completed は ROUTE_LOCKED、未確定 (draft/assigned) は ROUTE_NOT_CONFIRMED。
    unconfirm: oc
      .route({ method: "PUT", path: "/api/v1/routes/:publicId/unconfirm" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // レーン (ルート) の出発予定時刻 (デポ発) を手入力する。保存後に recalc して最新ルートを返す。
    // 設定時は再計算の出発基準として最優先され、route.optimize でも上書きされない。
    // null を送ると手入力を解除し、先頭 stop → 便区分開始時刻のフォールバックに戻る。
    // confirmed 以降のルートは ROUTE_LOCKED。
    // #695: この変更で新たに 3 時間ルール (集荷→配達の作業開始差) を超える注文が出る場合は
    // 保存せず元の出発時刻へ戻し RIDE_TIME_VIOLATION (422) を返す。
    setDeparture: oc
      .route({ method: "PUT", path: "/api/v1/routes/:publicId/departure" })
      .input(s.SetRouteDepartureInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // 立寄り順をユーザー指定の並びで保存し recalc する (自動最適化はしない)。
    // confirmed 以降のルートは ROUTE_LOCKED。
    // #695: この並びで新たに 3 時間ルールを超える注文が出る場合は保存せず元の順序へ戻し
    // RIDE_TIME_VIOLATION (422, data.order_numbers に対象注文番号) を返す。
    // 既に違反しているレーンを直す並べ替え (違反注文が増えない) は通す。
    reorderStops: oc
      .route({ method: "PUT", path: "/api/v1/routes/:publicId/reorder" })
      .input(s.ReorderStopsInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // #729: 区間 (直前 stop → 指定 stop) の高速道路利用可否を切り替え、その区間だけ
    // OSRM を高速ありで引き直して到着予定を再計算する。高速代は経費 (expense_type='highway')
    // として別途登録・請求する (本エンドポイントは金額を扱わない)。
    // #770: 確定直後 (confirmed) だけ ROUTE_LOCKED。出発後 (started) と完了後 (completed) は
    // 実走に合わせて直せる。completed は実績が確定しているので到着予定を再計算しない。
    // 請求書を発行済みの月は BILLING_CLOSED (422)。
    setStopAllowMotorway: oc
      .route({ method: "PUT", path: "/api/v1/routes/:publicId/stops/allow-motorway" })
      .input(s.SetStopAllowMotorwayInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // 未割当注文をレーンへ追加する。confirmed/started への緊急差し込みは許可し、completed は ROUTE_LOCKED。
    // #695: 追加後のレーンで新たに 3 時間ルールを超える注文が出る場合は追加を取り消して
    // 注文を未割当へ戻し RIDE_TIME_VIOLATION (422) を返す。
    addOrder: oc
      .route({ method: "POST", path: "/api/v1/routes/:routePublicId/orders" })
      .input(s.AddOrderToRouteInput.extend({ routePublicId: s.PublicIdSchema }))
      .output(
        z.object({
          data: s.Route,
          /** 追加後の自動最適化結果。失敗しても mutation は 200 (#526)。 */
          lane_optimize_status: s.LaneOptimizeStatus,
        }),
      ),

    // レーン (ルート) に設定済みの注文を取り外す. 紐づく全 stop を削除し注文を未割当 (confirmed) に戻す.
    removeOrder: oc
      .route({ method: "DELETE", path: "/api/v1/routes/:routePublicId/orders/:orderPublicId" })
      .input(z.object({ routePublicId: s.PublicIdSchema, orderPublicId: s.PublicIdSchema }))
      .output(
        z.object({
          data: s.Route,
          lane_optimize_status: s.LaneOptimizeStatus,
        }),
      ),

    // 注文 (order) 単位で別レーン (ルート) へ移動. pickup+delivery の両 stop をまとめて移す.
    // #695: 移動先レーンで新たに 3 時間ルールを超える注文が出る場合は移動元へ戻し
    // RIDE_TIME_VIOLATION (422) を返す。
    moveOrder: oc
      .route({
        method: "POST",
        path: "/api/v1/routes/:fromRoutePublicId/orders/:orderPublicId/move",
      })
      .input(
        s.MoveOrderInput.extend({
          fromRoutePublicId: s.PublicIdSchema,
          orderPublicId: s.PublicIdSchema,
        }),
      )
      .output(
        z.object({
          data: z.object({
            from_route: s.Route,
            to_route: s.Route,
            warnings: z.array(s.MoveStopWarning).default([]),
          }),
          /** 移動先レーンの自動最適化結果 (同一ルート内移動時はその 1 回分)。失敗しても mutation は 200 (#526)。 */
          lane_optimize_status: s.LaneOptimizeStatus,
        }),
      ),

    getPath: oc
      .route({ method: "GET", path: "/api/v1/routes/:publicId/path" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.RoutePath })),

    // レーン (ルート) 単体の経路最適化 (single-vehicle VRPTW). stop 集合は固定で順序のみ変更し、
    // pickup→delivery の前後関係・指定時間窓・帰着 leg を考慮して並べ替え → recalc 済みの最新ルートを返す.
    // confirmed 以降は ROUTE_LOCKED. optimizer 不達/解なし時は順序を変更せずエラー (OPTIMIZE_UNAVAILABLE / OPTIMIZE_INFEASIBLE).
    // クロスドック stop を含むルートは未対応 (OPTIMIZE_UNSUPPORTED).
    optimize: oc
      .route({ method: "POST", path: "/api/v1/routes/:publicId/optimize" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.Route })),

    // レーン (ルート) を削除する. 載っていた注文を未割当 (confirmed) に戻し stops とルート本体を削除する.
    // confirmed 以降のルートは ROUTE_LOCKED で拒否. 同一 plan 内の他ルートは触らない.
    delete: oc
      .route({ method: "DELETE", path: "/api/v1/routes/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  instruction: {
    status: oc
      .route({ method: "GET", path: "/api/v1/instructions/status" })
      .input(s.ListInstructionStatusInput)
      .output(z.object({ data: z.array(s.InstructionStatusItem) })),
  },

  driver: {
    todayRoutes: oc
      .route({ method: "GET", path: "/api/v1/driver/routes/today" })
      .input(s.ListDriverTodayRoutesInput)
      .output(
        z.object({
          data: z.array(s.Route),
          time_slot_boundaries: z.object({
            early_morning_until_time: z.string(),
            am_until_time: z.string(),
          }),
        }),
      ),

    updateStopStatus: oc
      .route({ method: "PUT", path: "/api/v1/driver/stops/:publicId/status" })
      .input(s.UpdateStopStatusInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.RouteStop })),

    // F14 Slice 3: ステータス巻き戻し (API-41a)
    revertStopStatus: oc
      .route({ method: "POST", path: "/api/v1/driver/stops/:publicId/status/revert" })
      .input(s.RevertStopStatusInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.RevertStopStatusOutput })),

    // F14 Slice 3: 打刻修正 (API-41b)
    editStopTimestamps: oc
      .route({ method: "PUT", path: "/api/v1/driver/stops/:publicId/status-timestamps" })
      // .and() は ZodIntersection になり .shape を失う → OpenAPILink が path param を抽出できない。
      // .extend() でプレーンな ZodObject を保つ (path param 展開の前提)。
      .input(s.EditStopTimestampsInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.EditStopTimestampsOutput })),

    // F14 Slice 2: 遅延報告 (API-41c)
    createDelayReport: oc
      .route({ method: "POST", path: "/api/v1/driver/stops/:publicId/delay-reports" })
      // .and() は ZodIntersection になり .shape を失う → OpenAPILink が path param を抽出できない。
      .input(s.CreateDelayReportInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DelayReport })),

    // #817: 過去日の配送実績の後追い登録。当日の打刻フロー (updateStopStatus) とは
    // 別経路にし、保存時点で route_stops を完了にする (運転日報の承認が確認手段)。
    createBackfillRequest: oc
      .route({ method: "POST", path: "/api/v1/driver/stops/:publicId/backfill-requests" })
      // .and() は ZodIntersection になり .shape を失う → OpenAPILink が path param を抽出できない。
      .input(s.CreateBackfillRequestInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BackfillRequest })),

    // #769: 自分が出した後追い登録の申請一覧 (申請中・承認済・却下を状態で絞れる)
    listBackfillRequests: oc
      .route({ method: "GET", path: "/api/v1/driver/backfill-requests" })
      .input(s.ListBackfillRequestsInput)
      .output(z.object({ data: z.array(s.BackfillRequest) })),

    // #769: 申請の取り下げ (pending のみ)
    cancelBackfillRequest: oc
      .route({ method: "POST", path: "/api/v1/driver/backfill-requests/:publicId/cancel" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BackfillRequest })),

    confirmInstruction: oc
      .route({ method: "POST", path: "/api/v1/instructions/:routePublicId/read" })
      .input(z.object({ routePublicId: s.PublicIdSchema }))
      .output(z.object({ data: s.MarkInstructionReadResult })),

    todayAttendance: oc.route({ method: "GET", path: "/api/v1/driver/attendance/today" }).output(
      z.object({
        data: s.DriverAttendance.nullable(),
        delivery_safety: s.DriverDeliverySafety,
        /**
         * 当日 (現在の勤務) の車両点検を実施済みか (#401)。
         * 初回出発打刻時の点検未実施警告に使う。未指定は false として扱う。
         */
        inspection_done: z.boolean().optional(),
      }),
    ),

    clockIn: oc
      .route({ method: "POST", path: "/api/v1/driver/attendance/clock-in" })
      .input(s.ClockInInput)
      .output(z.object({ data: s.DriverAttendance })),

    clockOut: oc
      .route({ method: "POST", path: "/api/v1/driver/attendance/clock-out" })
      .input(s.ClockOutInput)
      .output(z.object({ data: s.DriverAttendance })),

    // F10 リアルタイム: GPS 現在地報告。driver_locations へ書込み (24h TTL) し、
    // FleetHub DO へ location を publish して admin の地図に反映する。
    reportLocation: oc
      .route({ method: "POST", path: "/api/v1/driver/locations" })
      .input(s.ReportLocationInput)
      .output(z.object({ data: s.ReportLocationOutput })),

    listExpenses: oc
      .route({ method: "GET", path: "/api/v1/driver/expenses" })
      .input(s.ListDriverExpensesInput)
      .output(z.object({ data: z.array(s.DriverExpense) })),

    // #616: 経費の紐付け先候補 (対象日の自分の配達先)。後日入力のため過去日も取得できる。
    // /expenses/:publicId と衝突しないよう独立パスにしている。
    listExpenseStops: oc
      .route({ method: "GET", path: "/api/v1/driver/expense-stop-options" })
      .input(s.ListExpenseStopOptionsInput)
      .output(z.object({ data: z.array(s.ExpenseStopOption) })),

    createExpense: oc
      .route({ method: "POST", path: "/api/v1/driver/expenses" })
      .input(s.CreateExpenseInput)
      .output(z.object({ data: s.DriverExpense })),

    updateExpense: oc
      .route({ method: "PATCH", path: "/api/v1/driver/expenses/:publicId" })
      .input(s.UpdateExpenseInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DriverExpense })),

    deleteExpense: oc
      .route({ method: "DELETE", path: "/api/v1/driver/expenses/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),

    todayDailyReport: oc
      .route({ method: "GET", path: "/api/v1/driver/daily-report/today" })
      .output(z.object({ data: s.DriverDailyReport.nullable() })),

    submitDailyReport: oc
      .route({ method: "POST", path: "/api/v1/driver/daily-report/submit" })
      .input(s.SubmitDailyReportInput)
      .output(z.object({ data: s.DriverDailyReport })),

    listDailyReportRecoveryCandidates: oc
      .route({ method: "GET", path: "/api/v1/driver/daily-reports/recovery-candidates" })
      .input(s.ListDailyReportRecoveryCandidatesInput)
      .output(z.object({ data: z.array(s.DailyReportRecoveryCandidate) })),

    recoverDailyReport: oc
      .route({ method: "POST", path: "/api/v1/driver/daily-reports/recover" })
      .input(s.RecoverDailyReportInput)
      .output(z.object({ data: s.DriverDailyReport })),

    listDailyReports: oc
      .route({ method: "GET", path: "/api/v1/driver/daily-reports" })
      .input(s.ListDriverDailyReportsInput)
      .output(z.object({ data: z.array(s.DriverDailyReport) })),

    getDailyReport: oc
      .route({ method: "GET", path: "/api/v1/driver/daily-reports/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DriverDailyReport })),

    resubmitDailyReport: oc
      .route({ method: "POST", path: "/api/v1/driver/daily-reports/:publicId/resubmit" })
      .input(s.ResubmitDailyReportInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DriverDailyReport })),

    createVehicleInspection: oc
      .route({ method: "POST", path: "/api/v1/driver/vehicle-inspections" })
      .input(s.CreateVehicleInspectionInput)
      .output(z.object({ data: s.VehicleInspection })),

    createAlcoholCheck: oc
      .route({ method: "POST", path: "/api/v1/driver/alcohol-checks" })
      .input(s.CreateAlcoholCheckInput)
      .output(z.object({ data: s.AlcoholCheck })),

    listTodayRefuelings: oc
      .route({ method: "GET", path: "/api/v1/driver/refuelings/today" })
      .output(z.object({ data: z.array(s.DriverRefueling) })),

    createRefueling: oc
      .route({ method: "POST", path: "/api/v1/driver/refuelings" })
      .input(s.CreateRefuelingInput)
      .output(z.object({ data: s.DriverRefueling })),

    updateRefueling: oc
      .route({ method: "PATCH", path: "/api/v1/driver/refuelings/:publicId" })
      .input(s.UpdateRefuelingInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.DriverRefueling })),

    deleteRefueling: oc
      .route({ method: "DELETE", path: "/api/v1/driver/refuelings/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  parseJob: {
    list: oc
      .route({ method: "GET", path: "/api/v1/parse-jobs" })
      .input(s.ListParseJobsInput)
      .output(
        z.object({
          data: z.array(s.ParseJob),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/parse-jobs/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.ParseJob })),
  },

  invite: {
    create: oc
      .route({ method: "POST", path: "/api/v1/invites" })
      .input(s.CreateInviteInput)
      .output(z.object({ data: s.CreatedInvite })),

    list: oc
      .route({ method: "GET", path: "/api/v1/invites" })
      .input(s.ListInvitesInput)
      .output(s.ListInvitesOutput),

    reissue: oc
      .route({ method: "POST", path: "/api/v1/invites/reissue" })
      .input(s.ReissueInviteInput)
      .output(z.object({ data: s.CreatedInvite })),

    revoke: oc
      .route({ method: "POST", path: "/api/v1/invites/:publicId/revoke" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  optimization: {
    run: oc
      .route({ method: "POST", path: "/api/v1/optimization/run" })
      .input(s.RunOptimizationInput)
      .output(z.object({ data: s.RouteProposal })),

    getProposal: oc
      .route({ method: "GET", path: "/api/v1/optimization/proposals/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.RouteProposal })),

    adjust: oc
      .route({ method: "POST", path: "/api/v1/optimization/proposals/:publicId/adjust" })
      .input(s.AdjustProposalInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.RouteProposal })),

    confirm: oc
      .route({ method: "POST", path: "/api/v1/optimization/proposals/:publicId/confirm" })
      .input(s.ConfirmProposalInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.Route) })),

    listPublicationCandidates: oc
      .route({
        method: "GET",
        path: "/api/v1/optimization/plans/:planPublicId/publication-candidates",
      })
      .input(z.object({ planPublicId: s.PublicIdSchema }))
      .output(s.OptimizationPublicationCandidateList),

    getPublication: oc
      .route({ method: "GET", path: "/api/v1/optimization/proposals/:publicId/publication" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.OptimizationPublication })),

    publish: oc
      .route({ method: "POST", path: "/api/v1/optimization/proposals/:publicId/publish" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.OptimizationPublication })),

    previewVehicleAssignment: oc
      .route({ method: "POST", path: "/api/v1/optimization/preview-vehicle-assignment" })
      .input(s.PreviewVehicleAssignmentInput)
      .output(s.PreviewVehicleAssignmentOutput),
  },

  systemSettings: {
    get: oc
      .route({ method: "GET", path: "/api/v1/system-settings" })
      .output(z.object({ data: s.SystemSetting })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/system-settings" })
      .input(s.UpdateSystemSettingInput)
      .output(z.object({ data: s.SystemSetting })),
  },

  bankAccount: {
    list: oc
      .route({ method: "GET", path: "/api/v1/bank-accounts" })
      .output(z.object({ data: z.array(s.BankAccount) })),

    get: oc
      .route({ method: "GET", path: "/api/v1/bank-accounts/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BankAccount })),

    create: oc
      .route({ method: "POST", path: "/api/v1/bank-accounts" })
      .input(s.CreateBankAccountInput)
      .output(z.object({ data: s.BankAccount })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/bank-accounts/:publicId" })
      .input(s.UpdateBankAccountInput.safeExtend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BankAccount })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/bank-accounts/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),

    setDefault: oc
      .route({ method: "POST", path: "/api/v1/bank-accounts/:publicId/set-default" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BankAccount })),
  },

  invoice: {
    list: oc
      .route({ method: "GET", path: "/api/v1/invoices" })
      .input(s.ListInvoicesInput)
      .output(z.object({ data: z.array(s.InvoiceSummary), pagination: s.PaginationResponse })),

    get: oc
      .route({ method: "GET", path: "/api/v1/invoices/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    // F25 明細変更履歴: draft 編集で発生した明細の行単位変更を新しい順に返す (閲覧専用)。
    itemHistory: oc
      .route({ method: "GET", path: "/api/v1/invoices/:publicId/item-history" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.InvoiceItemChange) })),

    preview: oc
      .route({ method: "POST", path: "/api/v1/invoices/preview" })
      .input(s.PreviewInvoiceInput)
      .output(z.object({ data: s.PreviewInvoiceOutput })),

    create: oc
      .route({ method: "POST", path: "/api/v1/invoices" })
      .input(s.CreateInvoiceInput)
      .output(z.object({ data: s.InvoiceDetail })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/invoices/:publicId" })
      .input(s.UpdateInvoiceInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    // F25 状態遷移専用エンドポイント。issued 以降の編集は PUT では 422 となり、
    // 状態変更はこれらの遷移エンドポイント経由のみで行う。
    issue: oc
      .route({ method: "POST", path: "/api/v1/invoices/:publicId/issue" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    send: oc
      .route({ method: "POST", path: "/api/v1/invoices/:publicId/send" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    markPaid: oc
      .route({ method: "POST", path: "/api/v1/invoices/:publicId/mark-paid" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    void: oc
      .route({ method: "POST", path: "/api/v1/invoices/:publicId/void" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    // 誤操作リカバリ: ステータスを1段前に戻し、戻し先より後の遷移情報をクリアする
    // (paid→issued / sent→issued / issued→draft / void→draft)。
    revertStatus: oc
      .route({ method: "POST", path: "/api/v1/invoices/:publicId/revert-status" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.InvoiceDetail })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/invoices/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  news: {
    previewSend: oc
      .route({ method: "POST", path: "/api/v1/news/preview-send" })
      .input(s.PreviewNewsSendInput)
      .output(s.PreviewNewsSendOutput),

    send: oc
      .route({ method: "POST", path: "/api/v1/news/send" })
      .input(s.SendNewsInput)
      .output(s.SendNewsOutput),

    list: oc
      .route({ method: "GET", path: "/api/v1/news" })
      .input(s.ListNewsInput)
      .output(
        z.object({
          data: z.array(s.NewsItem),
          total: z.number(),
          pagination: s.PaginationResponse,
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/news/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.NewsItem })),

    create: oc
      .route({ method: "POST", path: "/api/v1/news" })
      .input(s.CreateNewsInput)
      .output(z.object({ data: s.NewsItem })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/news/:publicId" })
      .input(s.UpdateNewsInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.NewsItem })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/news/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  line: {
    pushStats: oc
      .route({ method: "GET", path: "/api/v1/line/push-stats" })
      .input(s.GetLinePushStatsInput)
      .output(z.object({ data: s.LinePushStats })),

    sendLogs: oc
      .route({ method: "GET", path: "/api/v1/line/send-logs" })
      .input(s.ListLineSendLogsInput)
      .output(z.object({ data: z.array(s.LineSendLog), pagination: s.PaginationResponse })),

    webhookLogs: oc
      .route({ method: "GET", path: "/api/v1/line/webhooks/logs" })
      .input(s.ListLineWebhookLogsInput)
      .output(z.object({ data: z.array(s.LineWebhookLog), pagination: s.PaginationResponse })),
  },

  notification: {
    list: oc
      .route({ method: "GET", path: "/api/v1/notifications" })
      .input(s.ListNotificationsInput)
      .output(z.object({ data: z.array(s.Notification), unread_count: z.number() })),

    markRead: oc
      .route({ method: "POST", path: "/api/v1/notifications/:publicId/read" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),

    markAllRead: oc
      .route({ method: "POST", path: "/api/v1/notifications/read-all" })
      .output(z.object({ success: z.literal(true) })),
  },

  notificationSettings: {
    // 現在ログインユーザー自身の通知設定 (チャネル別 ON/OFF) を取得・更新する.
    get: oc
      .route({ method: "GET", path: "/api/v1/settings/notifications" })
      .output(z.object({ data: s.NotificationSettings })),

    update: oc
      .route({ method: "PUT", path: "/api/v1/settings/notifications" })
      .input(s.UpdateNotificationSettingsInput)
      .output(z.object({ data: s.NotificationSettings })),
  },

  fleet: {
    // F10 (Issue #503): ドライバーごとの最終観測位置 (driver_locations の最新行、24h TTL 内)。
    // admin ドライバー状況画面が初期スナップショットに使う。ライブは /api/v1/admin/fleet/stream (WS)。
    latestLocations: oc
      .route({ method: "GET", path: "/api/v1/admin/fleet/locations" })
      .output(z.object({ data: z.array(s.DriverLatestLocation) })),
  },

  dashboard: {
    stats: oc
      .route({ method: "GET", path: "/api/v1/dashboard/stats" })
      .input(s.DashboardStatsInput)
      .output(z.object({ data: s.DashboardStats })),

    alerts: oc
      .route({ method: "GET", path: "/api/v1/dashboard/alerts" })
      .input(s.ListDashboardAlertsInput)
      .output(z.object({ data: z.array(s.DashboardAlert), total: z.number() })),
  },

  alert: {
    list: oc
      .route({ method: "GET", path: "/api/v1/alerts" })
      .input(s.ListBusinessAlertsInput)
      .output(s.BusinessAlertListResponse),
  },

  report: {
    list: oc
      .route({ method: "GET", path: "/api/v1/reports" })
      .input(
        z.object({
          limit: z.coerce.number().int().min(1).max(200).default(50),
          date: z.string().optional(),
          status: z.string().optional(),
          driver_public_id: z.string().optional(),
        }),
      )
      .output(z.object({ data: z.array(s.DriverDailyReport), pagination: s.PaginationResponse })),

    review: oc
      .route({ method: "POST", path: "/api/v1/reports/:publicId/review" })
      // input は必ずプレーンな z.object にする。.superRefine()/.refine() で ZodEffects に
      // ラップすると OpenAPILink が path param (:publicId) を input から抽出できず、
      // URL が未展開のまま送られて 404 になる (他の :publicId エンドポイントは全て素の object)。
      // reject 時の rejection_note 必須チェックはハンドラ側 (routers/index.ts) で実施済み。
      .input(
        z.object({
          publicId: z.string().min(1),
          action: z.enum(["approve", "reject"]),
          rejection_note: z.string().nullable().optional(),
          // F08: 承認時に管理者が実走行距離 (km) を補正できる。距離突合/請求の初期値に反映。
          actual_distance_km: z.string().nullable().optional(),
        }),
      )
      .output(z.object({ data: s.DriverDailyReport })),

    // #829: 管理者 (dispatcher) が運転日報の内容を直接編集する。
    // 差戻し → ドライバー修正 → 再提出の往復を経ずに軽微な誤記を直せるようにする。
    // input は必ずプレーンな z.object にする (review と同じ理由。ZodEffects にすると
    // OpenAPILink が path param を抽出できず URL が未展開のまま送られて 404 になる)。
    update: oc
      .route({ method: "PATCH", path: "/api/v1/reports/:publicId" })
      .input(s.UpdateDailyReportInput.extend({ publicId: z.string().min(1) }))
      .output(z.object({ data: s.DriverDailyReport })),
  },

  // F14: 配送実行ファミリー (管理画面 Sheet 用)
  deliveryExecution: {
    getStopStatusTarget: oc
      .route({ method: "GET", path: "/api/v1/driver/stops/:publicId/status-target" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.StopStatusTarget })),

    resolveDelayReports: oc
      .route({ method: "POST", path: "/api/v1/driver/stops/:publicId/delay-reports/resolve" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.ResolveDelayReportsOutput })),

    // F14 Slice 1: 配送ステータス変更履歴一覧 (admin/office_staff)
    listStopHistory: oc
      .route({ method: "GET", path: "/api/v1/driver/stops/:publicId/history" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.DeliveryStatusHistory) })),

    // F14 Slice 3: 申請履歴取得 (API-43a、admin/office_staff のみ)
    getStopAudit: oc
      .route({ method: "GET", path: "/api/v1/driver/stops/:publicId/audit" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.StopAuditTimeline })),

    // #769: 後追い登録の可否判定. driver は自分の stop のみ、admin/office_staff は全 stop 可。
    // UI は判定ロジックを持たず、この結果だけを見て導線を出すか決める。
    getBackfillEligibility: oc
      .route({ method: "GET", path: "/api/v1/driver/stops/:publicId/backfill-eligibility" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BackfillEligibility })),

    // F14 Slice 4: 配達証跡画像一覧. driver は自分の stop のみ、admin/office_staff は全 stop 可
    listStopProof: oc
      .route({ method: "GET", path: "/api/v1/driver/stops/:publicId/proof" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.ProofImage) })),
  },

  // #769: 過去日の配送実績の後追い登録 (承認・代理入力)。admin/office_staff のみ。
  // 期間ゲート (承認済み運転日報・発行済み請求書) はすべてサーバ側で判定する。
  deliveryBackfill: {
    listRequests: oc
      .route({ method: "GET", path: "/api/v1/admin/backfill-requests" })
      .input(s.ListBackfillRequestsInput)
      .output(z.object({ data: z.array(s.BackfillRequest) })),

    reviewRequest: oc
      .route({ method: "POST", path: "/api/v1/admin/backfill-requests/:publicId/review" })
      .input(s.ReviewBackfillRequestInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BackfillRequest })),

    // 管理者・事務員の代理入力。申請 + 即承認を 1 操作で行い、申請経路と同じ監査を残す。
    backfillStop: oc
      .route({ method: "POST", path: "/api/v1/admin/stops/:publicId/backfill" })
      .input(s.AdminBackfillStopInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.BackfillRequest })),
  },

  // 住所ジオコーディング: admin の住所入力欄から呼ぶ順ジオ (住所 → 緯度経度)。
  // ブラウザから外部 API を直叩きせず、API が Yahoo!ジオコーダ (地番・戸番レベル) を優先し、
  // デジタル庁 abr-geocoder にフォールバックしてプロキシする (Issue #612)。
  // 両方未設定 / ヒットなしは data: null を返す (呼出側で地図手動補正)。
  // attribution: 座標を出したソースの出典/クレジット (Yahoo クレジット or ABR CC BY 4.0)。
  // 利用規約上必須のため data の有無に関わらず全レスポンスに載せる (LLM/外部へ伝搬しても出典が残る設計)。
  geocoding: {
    forward: oc
      .route({ method: "GET", path: "/api/v1/geocoding/forward" })
      .input(z.object({ address: z.string().min(1).max(200) }))
      .output(
        z.object({
          data: z
            .object({
              lat: z.number(),
              lng: z.number(),
              match_level: z.string().nullable(),
              needs_manual_confirmation: z.literal(false),
            })
            .nullable(),
          candidate: z
            .object({
              lat: z.number(),
              lng: z.number(),
              match_level: z.string().nullable(),
              needs_manual_confirmation: z.literal(true),
            })
            .nullable(),
          attribution: z.string(),
        }),
      ),
  },

  // F29 業務日報 (取引先別配送連絡表)。設計書: docs/08_work_report/01_design.md
  workReport: {
    list: oc
      .route({ method: "GET", path: "/api/v1/work-reports" })
      .input(s.ListWorkReportInput)
      .output(z.object({ data: s.ListWorkReportOutput })),

    generate: oc
      .route({ method: "POST", path: "/api/v1/work-reports/generate" })
      .input(s.GenerateWorkReportInput)
      .output(z.object({ data: s.GenerateWorkReportOutput })),

    // #616: 注文単位の経費 (高速代・駐車料金) を既存明細の実費欄へ再取込する。
    // 後日確定した ETC を生成済みの月次日報へ反映するための操作。手修正行は上書きしない。
    syncExpenses: oc
      .route({ method: "POST", path: "/api/v1/work-reports/expenses/sync" })
      .input(s.SyncWorkReportExpensesInput)
      .output(z.object({ data: s.SyncWorkReportExpensesOutput })),

    createEntry: oc
      .route({ method: "POST", path: "/api/v1/work-reports/entries" })
      .input(s.CreateWorkReportEntryInput)
      .output(z.object({ data: s.WorkReportEntry })),

    updateEntry: oc
      .route({ method: "PUT", path: "/api/v1/work-reports/entries/:publicId" })
      .input(s.UpdateWorkReportEntryInput)
      .output(z.object({ data: s.WorkReportEntry })),

    deleteEntry: oc
      .route({ method: "DELETE", path: "/api/v1/work-reports/entries/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),

    // #627: 距離突合 (F27) を廃止し、注文別請求距離の算出・補正・確認を業務日報へ一本化した。
    // 生成フローからは従来どおり OSRM を呼ばない (docs/08_work_report/01_design.md §5)。
    // 算出は担当者の明示操作で走らせ、月次生成が OSRM の応答時間・失敗に引きずられないようにする。
    calculateEntryDistances: oc
      .route({ method: "POST", path: "/api/v1/work-reports/distances/calculate" })
      .input(s.CalculateWorkReportDistancesInput)
      .output(z.object({ data: s.CalculateWorkReportDistancesOutput })),

    // 補正は理由必須。確定距離を動かした事実を監査として残す。
    updateEntryDistance: oc
      .route({ method: "PATCH", path: "/api/v1/work-reports/entries/:publicId/distance" })
      .input(s.UpdateWorkReportEntryDistanceInput)
      .output(z.object({ data: s.WorkReportEntry })),

    // #667 / #642: 便の請求距離・支払距離の補正。運賃に効くのは便の距離なので、OSRM 失敗時もここから手入力する。
    updateTripDistance: oc
      .route({ method: "PATCH", path: "/api/v1/work-reports/trips/:publicId/distance" })
      .input(s.UpdateWorkReportTripDistanceInput)
      .output(z.object({ data: s.WorkReportEntryTrip })),

    // 確認は注文ごとの参考距離。請求書の発行ゲートは便の距離を見る (#668)。
    confirmEntryDistances: oc
      .route({ method: "POST", path: "/api/v1/work-reports/distances/confirm" })
      .input(s.ConfirmWorkReportDistancesInput)
      .output(z.object({ data: s.ConfirmWorkReportDistancesOutput })),

    listFarePlans: oc
      .route({ method: "GET", path: "/api/v1/clients/:publicId/fare-plans" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: z.array(s.FarePlan) })),

    upsertFarePlan: oc
      .route({ method: "PUT", path: "/api/v1/clients/:publicId/fare-plans" })
      .input(s.UpsertFarePlanInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.FarePlan })),

    deleteFarePlan: oc
      .route({ method: "DELETE", path: "/api/v1/fare-plans/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),

    // #708: 取引先の休業日。チャーター定額枠は稼働日 (平日かつ休業日でない日) にだけ配る。
    // 土日は曜日で判定するので登録不要。祝日・GW・お盆・年末年始は休業日として登録する。
    listClosedDays: oc
      .route({ method: "GET", path: "/api/v1/clients/:publicId/closed-days" })
      .input(s.ListClientClosedDaysInput)
      .output(z.object({ data: z.array(s.ClientClosedDay) })),

    // 月単位の全置換。画面がその月のカレンダーで編集するので確定値を送らせる。
    replaceClosedDays: oc
      .route({ method: "PUT", path: "/api/v1/clients/:publicId/closed-days" })
      .input(s.ReplaceClientClosedDaysInput)
      .output(z.object({ data: z.array(s.ClientClosedDay) })),

    getConfig: oc
      .route({ method: "GET", path: "/api/v1/clients/:publicId/work-report-config" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.GetWorkReportConfigOutput })),

    updateConfig: oc
      .route({ method: "PUT", path: "/api/v1/clients/:publicId/work-report-config" })
      .input(s.UpdateWorkReportConfigInput)
      .output(z.object({ data: s.GetWorkReportConfigOutput })),

    // F25 取引先別 請求書 Excel 様式設定 (Issue #267)
    getInvoiceExcelConfig: oc
      .route({ method: "GET", path: "/api/v1/clients/:publicId/invoice-excel-config" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.GetInvoiceExcelConfigOutput })),

    updateInvoiceExcelConfig: oc
      .route({ method: "PUT", path: "/api/v1/clients/:publicId/invoice-excel-config" })
      .input(s.UpdateInvoiceExcelConfigInput)
      .output(z.object({ data: s.GetInvoiceExcelConfigOutput })),
  },

  // Admin 向け勤怠読取 (admin/office_staff のみ)
  attendance: {
    list: oc
      .route({ method: "GET", path: "/api/v1/admin/attendances" })
      .input(s.ListAdminAttendancesInput)
      .output(z.object({ data: z.array(s.AdminAttendance), pagination: s.PaginationResponse })),

    // 整合 L-1: 管理者による勤怠是正 (退勤打刻忘れの代理修正)。admin/office_staff のみ。
    // 修正後に運転日報を再生成し F29 支払距離 / F27 実距離を復元する。
    correct: oc
      .route({ method: "PATCH", path: "/api/v1/admin/attendances/:publicId" })
      .input(s.CorrectAttendanceInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.AdminAttendance })),
  },

  // Admin 向け経費読取 (admin/office_staff のみ)
  expense: {
    list: oc
      .route({ method: "GET", path: "/api/v1/admin/expenses" })
      .input(s.ListAdminExpensesInput)
      .output(
        z.object({
          data: z.array(s.AdminExpense),
          pagination: s.PaginationResponse,
          /** #537: 対象期間全体の合計金額。ページングしても画面の合計が変わらないようにする。 */
          total_amount: z.number().int().default(0),
        }),
      ),

    get: oc
      .route({ method: "GET", path: "/api/v1/admin/expenses/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.AdminExpense })),

    // #616: 事務による代理入力・修正。ETC のように金額が後日確定する経費を管理側で補完する。
    // ドライバー本人と違い、日報が承認済み (締め後) でも登録・修正できる。
    listStops: oc
      .route({ method: "GET", path: "/api/v1/admin/expense-stop-options" })
      .input(s.ListAdminExpenseStopOptionsInput)
      .output(z.object({ data: z.array(s.ExpenseStopOption) })),

    // #616: 注文詳細から経費を登録するための紐付け先。担当ドライバー・配送日も含めて返すので、
    // 呼出側は注文だけ知っていればダイアログの対象者・対象日・配達先を確定できる。
    listStopsByOrder: oc
      .route({ method: "GET", path: "/api/v1/admin/orders/:publicId/expense-stops" })
      .input(s.ListOrderExpenseStopsInput)
      .output(z.object({ data: z.array(s.ExpenseStopOption) })),

    create: oc
      .route({ method: "POST", path: "/api/v1/admin/expenses" })
      .input(s.CreateAdminExpenseInput)
      .output(z.object({ data: s.AdminExpense })),

    update: oc
      .route({ method: "PATCH", path: "/api/v1/admin/expenses/:publicId" })
      .input(s.UpdateAdminExpenseInput.extend({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.AdminExpense })),

    delete: oc
      .route({ method: "DELETE", path: "/api/v1/admin/expenses/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ success: z.literal(true) })),
  },

  // Admin 向け給油読取 (admin/office_staff のみ)
  refueling: {
    list: oc
      .route({ method: "GET", path: "/api/v1/admin/refuelings" })
      .input(s.ListAdminRefuelingsInput)
      .output(z.object({ data: z.array(s.AdminRefueling), pagination: s.PaginationResponse })),

    get: oc
      .route({ method: "GET", path: "/api/v1/admin/refuelings/:publicId" })
      .input(z.object({ publicId: s.PublicIdSchema }))
      .output(z.object({ data: s.AdminRefueling })),
  },
});

export type Contract = typeof contract;

/**
 * 契約から導出した oRPC クライアント型 (型の単一源泉)。
 * OpenAPI 経由のため日付等は JSON 文字列化される点に注意 (@carry/client の CarryApiClient で JsonifiedClient ラップ)。
 */
export type ContractClient = ContractRouterClient<typeof contract>;
